import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { KioskButton } from '../components/KioskButton';
import { KioskFrame } from '../components/KioskFrame';
import { checkDevice } from '../lib/api';
import { clearDeviceCode, getDeviceCode, setDeviceCode } from '../lib/device-identity';

/**
 * 機台設定（維運用，非顧客動線）— runtime 綁定本機 device_code。
 *
 * 入口：佈建時開 `?device=KIOSK-02`（自動綁定），或 Attract 畫面右下角連點 5 次。
 *
 * 綁定前先呼叫 GET /kiosk/device-check 驗證：device_code 必須**已於後台 CMS 登錄**、
 * 未退役、且為 kiosk 類型才能綁定（設備生命週期：CMS 登錄 → 機台認領 → 上線）。
 * 未登錄 / 已退役 / 非 kiosk 一律擋下並提示，而非等首次 heartbeat 才默默失敗。
 * 綁定成功後 useKioskHeartbeat 即時切換回報目標。
 *
 * ## 進頁時會重驗目前的綁定
 *
 * 綁定當下驗過不代表之後一直有效——設備在 CMS 被退役、或後台資料重建，代號就
 * 不再存在，心跳從此失敗而機台上完全看不出來（只有 CMS 顯示離線）。維運會來這一頁
 * 正是因為儀表板顯示離線，所以這裡直接把原因講出來，不用他再猜。
 */
export function DeviceSetupScreen(): JSX.Element {
  const navigate = useNavigate();
  const current = getDeviceCode();
  const [input, setInput] = useState<string>(current ?? '');
  const [saved, setSaved] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bindingHealth, setBindingHealth] = useState<string | null>(null);

  // 進頁即重驗目前綁定；未綁定或驗不到（後端離線）就不下結論
  useEffect(() => {
    if (current === null) return;
    let cancelled = false;
    checkDevice(current).then(
      (r) => {
        if (cancelled) return;
        if (!r.exists) setBindingHealth(`此代號已不存在於後台，心跳無法回報`);
        else if (r.retired) setBindingHealth('此代號已退役，心跳無法回報');
        else if (!r.bindable) setBindingHealth(`此代號類型為 ${r.device_type ?? '未知'}，非購票機`);
        else setBindingHealth(null);
      },
      () => {
        /* 後端連不上：不能據此判定綁定有誤，維持沉默 */
      },
    );
    return () => {
      cancelled = true;
    };
  }, [current]);

  const trimmed = input.trim();
  const canSave = trimmed !== '' && trimmed !== current && !checking;

  async function save(): Promise<void> {
    setChecking(true);
    setError(null);
    setSaved(null);
    try {
      const r = await checkDevice(trimmed);
      if (!r.exists) {
        setError(`代號 ${trimmed} 尚未於後台登錄；請先在 CMS「設備管理」新增此設備`);
        return;
      }
      if (r.retired) {
        setError(`代號 ${trimmed} 已退役，無法綁定`);
        return;
      }
      if (!r.bindable) {
        setError(`代號 ${trimmed} 非購票機（類型：${r.device_type ?? '未知'}），無法綁定`);
        return;
      }
      setDeviceCode(trimmed);
      setSaved(trimmed);
    } catch {
      setError('驗證失敗，請確認與後端連線狀態');
    } finally {
      setChecking(false);
    }
  }

  function unbind(): void {
    clearDeviceCode();
    setInput('');
    setSaved(null);
    setError(null);
  }

  return (
    <KioskFrame>
      <h1 className="k-h1">機台設定</h1>
      <p className="k-text k-text--dim" style={{ marginBottom: 24 }}>
        設定本台購票機的代號（device_code），用於設備儀表板辨識與在線回報。 代號需先在後台
        CMS「設備管理」登錄；同一份系統部署多台時，每台請各自綁定不同代號。
      </p>

      <div className="k-card" style={{ textAlign: 'left' }}>
        <div className="k-row" style={{ marginBottom: 16 }}>
          <span className="k-text" style={{ fontWeight: 600 }}>
            目前綁定
          </span>
          <span className="k-text" style={{ fontFamily: 'monospace' }}>
            {current ?? '（未綁定）'}
          </span>
        </div>

        {bindingHealth !== null && (
          <p
            className="k-text"
            data-testid="kiosk-binding-invalid"
            style={{ marginBottom: 16, color: 'var(--c-danger)', fontWeight: 600 }}
          >
            ⚠ {bindingHealth}——請在 CMS「設備管理」登錄此代號，或於下方改綁其他代號。
          </p>
        )}

        <label className="k-text" style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
          機台代號
        </label>
        <input
          className="k-input"
          value={input}
          placeholder="例：KIOSK-02"
          onChange={(e) => {
            setInput(e.target.value);
            setSaved(null);
            setError(null);
          }}
          style={{
            width: '100%',
            padding: '12px 14px',
            fontSize: 20,
            fontFamily: 'monospace',
            border: '1px solid var(--c-border)',
            borderRadius: 8,
            boxSizing: 'border-box',
          }}
        />
        <p className="k-text k-text--dim" style={{ marginTop: 8, fontSize: 14 }}>
          綁定時會驗證此代號是否已於後台登錄且為購票機；未登錄無法綁定（不影響購票）。
        </p>

        {error !== null && (
          <p
            className="k-text"
            style={{ marginTop: 12, color: 'var(--c-danger, #b91c1c)', fontWeight: 600 }}
          >
            ⚠ {error}
          </p>
        )}
        {saved !== null && (
          <p
            className="k-text"
            style={{ marginTop: 12, color: 'var(--c-success)', fontWeight: 600 }}
          >
            ✓ 已綁定為 {saved}，開始回報在線
          </p>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <KioskButton variant="primary" disabled={!canSave} onClick={() => void save()}>
            {checking ? '驗證中…' : '儲存綁定'}
          </KioskButton>
          <KioskButton variant="secondary" disabled={current === null} onClick={unbind}>
            解除綁定
          </KioskButton>
        </div>
      </div>

      <div className="k-footbar">
        <KioskButton variant="secondary" onClick={() => navigate('/', { replace: true })}>
          ← 返回
        </KioskButton>
      </div>
    </KioskFrame>
  );
}
